import { useState, useEffect, useCallback, useRef } from 'react';
import {
    loadSettings,
    saveSettings,
    getDefaults,
    isNotificationSupported,
    getPermissionState,
    requestPermission as requestPerm,
    registerSW,
    checkAndFireReminder,
} from './notificationUtils';
import { loadComplianceData } from '../compliance/complianceUtils';

export function useNotificationSettings() {
    const [settings, setSettings] = useState(getDefaults);
    const [permissionState, setPermissionState] = useState('default');
    const [supported, setSupported] = useState(true);
    const timerRef = useRef(null);

    // Load on mount
    useEffect(() => {
        setSettings(loadSettings());
        setSupported(isNotificationSupported());
        setPermissionState(getPermissionState());

        // Fire reminder check on app open
        const complianceData = loadComplianceData();
        const msUntilReminder = checkAndFireReminder(complianceData);

        if (msUntilReminder && msUntilReminder > 0) {
            timerRef.current = setTimeout(() => {
                const freshData = loadComplianceData();
                checkAndFireReminder(freshData);
            }, msUntilReminder);
        }

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const updateSettings = useCallback((partial) => {
        setSettings((prev) => {
            const next = {
                ...prev,
                ...partial,
                types: { ...prev.types, ...(partial.types || {}) },
            };
            saveSettings(next);
            return next;
        });
    }, []);

    const requestPermission = useCallback(async () => {
        const result = await requestPerm();
        setPermissionState(result);
        if (result === 'granted') {
            await registerSW();
        }
        return result;
    }, []);

    const toggleEnabled = useCallback(async () => {
        if (!settings.enabled) {
            // Turning ON — request permission first
            const result = await requestPermission();
            if (result === 'granted') {
                updateSettings({ enabled: true });
                return true;
            }
            // If denied or dismissed, don't enable
            return false;
        } else {
            // Turning OFF
            updateSettings({ enabled: false });
            return true;
        }
    }, [settings.enabled, requestPermission, updateSettings]);

    return {
        settings,
        updateSettings,
        permissionState,
        supported,
        requestPermission,
        toggleEnabled,
    };
}
